import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { Api } from '../../../api/gse/api';
import {
  getAllConsumerList,
  GetAllConsumerList$Params,
  getAllRechargeHistory,
  GetAllRechargeHistory$Params,
  getPettyCashAccount,
  GetPettyCashAccount$Params,
  updatePettyCashBalance,
  UpdatePettyCashBalance$Params,
} from '../../../api/gse/functions';
import { GetPettyCashConsumerDataResponse } from '../../../api/gse/models/get-petty-cash-consumer-data-response';
import { GetPettyCashRechargeHistoryResponse } from '../../../api/gse/models/get-petty-cash-recharge-history-response';
import { PettyCashAccountResponse } from '../../../api/gse/models/petty-cash-account-response';
import { UpdatePettyCashBalanceRequest } from '../../../api/gse/models/update-petty-cash-balance-request';
import { StrictHttpResponse } from '../../../api/gse/strict-http-response';
import { PettyCashUpdateRequest } from '../../../features/gse/petty-cash/models/petty-cash.model';

@Injectable({
  providedIn: 'root',
})
export class PettyCashService {
  private api = inject(Api);

  /**
   * Get current petty cash balance
   */
  getPettyCashBalance(): Observable<PettyCashAccountResponse> {
    const params: GetPettyCashAccount$Params = {};
    return from(this.api.invoke(getPettyCashAccount, params));
  }

  getPettyCashBalanceByWarehouses(wareHouseIds: number[]): Observable<PettyCashAccountResponse> {
    const params: GetPettyCashAccount$Params = {
      wareHouseId: wareHouseIds.length > 0 ? wareHouseIds : undefined,
    };
    return from(this.api.invoke(getPettyCashAccount, params));
  }

  /**
   * GET /petty-cash-accounts/{id}/consumers — server-paged consumer (replenishment) list.
   */
  getConsumerList(
    accountId: number,
    options: Omit<GetAllConsumerList$Params, 'id'>
  ): Observable<GetPettyCashConsumerDataResponse> {
    const params: GetAllConsumerList$Params = {
      id: accountId,
      ...options,
    };
    return from(this.api.invoke(getAllConsumerList, params));
  }

  /**
   * GET /petty-cash-accounts/{id}/recharge-history — server-paged list for the petty cash account.
   */
  getRechargeHistory(
    accountId: number,
    options: Omit<GetAllRechargeHistory$Params, 'id'>
  ): Observable<GetPettyCashRechargeHistoryResponse> {
    const params: GetAllRechargeHistory$Params = {
      id: accountId,
      ...options,
    };
    return from(this.api.invoke(getAllRechargeHistory, params));
  }

  /**
   * Get petty cash balance with response
   */
  getPettyCashBalanceResponse(
    wareHouseIds?: number[]
  ): Observable<StrictHttpResponse<PettyCashAccountResponse>> {
    const params: GetPettyCashAccount$Params = {
      wareHouseId: wareHouseIds && wareHouseIds.length > 0 ? wareHouseIds : undefined,
    };
    return from(this.api.invoke$Response(getPettyCashAccount, params));
  }

  /**
   * POST /petty-cash-accounts — add to petty cash balance (amount in minor units per API).
   */
  updatePettyCashBalance(
    body: PettyCashUpdateRequest
  ): Observable<StrictHttpResponse<PettyCashAccountResponse>> {
    const updateRequest: UpdatePettyCashBalanceRequest = {
      amount: body.amount,
    };
    if (body.accountHolderId != null) {
      updateRequest.accountHolderId = body.accountHolderId;
    }
    if (body.wareHouseId != null) {
      updateRequest.wareHouseId = body.wareHouseId;
    }
    if (body.comment != null && String(body.comment).trim() !== '') {
      updateRequest.comment = String(body.comment).trim();
    }
    const params: UpdatePettyCashBalance$Params = { body: updateRequest };
    return from(this.api.invoke$Response(updatePettyCashBalance, params));
  }
}
